import { CSSProperties, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Drawer, withStyles } from '@material-ui/core';
import {
  AIIcon,
  FilterIcon,
  SearchIcon,
  EmailIcon
} from '@setvi/shared/components/sicons';
import { SearchType } from '@setvi/shared/components/intelligence/search/interfaces';
import { pricingDependsOnCustomer } from 'componentsV2/product/details/content/components/header/components';
import { useDialog } from '@setvi/shared/providers';
import { Product } from '@setvi/shared/interfaces';
import { useComposeEmail } from 'providersV2/compose-email/context';
import { EditorProduct } from '@setvi/shared/components/seditor/interfaces';
import { getProductCardAsHtml } from '@setvi/shared/components/seditor/hooks/use-editor-products/heplers';
import {
  AnalyticsAppType,
  AnalyticsModules,
  useAnalytics
} from '@setvi/shared/hooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  FloatMenu,
  Header,
  MobileProductList,
  MobileCategories,
  MobileFilters,
  AskAIInput,
  HeaderProps,
  MobileFiltersProps,
  MobileProductsList,
  MobileProductDetails,
  MobileProductDetailsProps,
  MobileEditor
} from './components';
import { CategoriesContentProps } from '../components/bottom-left-content/categories';
import { PromotionDialog } from '../components';
import {
  MobileEmailSelected,
  MobileClearSelected
} from './components/drawer-content';
import { parseProductsToEmailProduct, ProductActionType } from '../utils';

export enum MobileDrawerContent {
  Categories = 'Categories',
  Filters = 'Filters',
  Search = 'Search'
}

export const Container = withStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: 'rgb(248, 249, 250)',
    overflow: 'hidden'
  }
})(Box);

export const mobileDriverStyle = {
  borderTopLeftRadius: 16,
  borderTopRightRadius: 16,
  boxSizing: 'border-box',
  backgroundColor: '#F8F9FA',
  paddingTop: 16,
  padding: 20,
  height: '97%'
} as CSSProperties;

interface MobileCatalog {
  controller?: any;
  headerProps: Pick<
    HeaderProps,
    | 'selectedCategory'
    | 'feedback'
    | 'refetchProducts'
    | 'handleSearch'
    | 'onSelectCategory'
  >;
  productsListProps: Omit<
    MobileProductsList,
    | 'pricingList'
    | 'onPromotionClick'
    | 'handleEmail'
    | 'composeEmailValues'
    | 'feedback'
    | 'viewMode'
  >;
  categoriesProps: CategoriesContentProps;
  filtersProps: Omit<
    MobileFiltersProps,
    'filters' | 'handleClose' | 'aiSearchActive'
  >;
  productDetailsProps: Omit<
    MobileProductDetailsProps,
    'pricingList' | 'onPromotionClick' | 'productCardProps' | 'handleEmail'
  >;
}

enum EmailDrawerContents {
  Email = 'Email',
  ClearSelected = 'CrearSelected'
}

const getSelectedProductsHTML = (products: Product[]): string => {
  const parsedProducts = parseProductsToEmailProduct(products);
  return parsedProducts
    .map(product => getProductCardAsHtml(product, false))
    .join('');
};

export const MobileCatalog = ({
  controller,
  headerProps,
  productsListProps,
  categoriesProps,
  filtersProps,
  productDetailsProps
}: MobileCatalog) => {
  const flags = useFlags();
  const [drawerContent, setDrawerContent] = useState(null);
  const [searchContent, setSearchContent] = useState<SearchType>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAIInput, setShowAIInput] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [searchParams] = useSearchParams();
  const isEmbedded = searchParams.get('ipad') === 'true';
  // const [viewMode, setViewMode] = useState(
  //   (searchParams.get('view') as Views) || Views.LIST_VIEW
  // );

  // This state exist because of animation when the user closes popup
  const [drawerHeight, setDrawerHeight] =
    useState<CSSProperties['height']>('90%');
  const { openDialog, closeDialog } = useDialog();
  const { open } = useComposeEmail();
  const { captureAnalytics } = useAnalytics();

  const selectedProducts = productsListProps.selectedProducts ?? [];
  const customer = productsListProps?.filters?.customer?.value;
  const priceGroup = productsListProps?.filters?.priceGroup?.value;
  const configPricing = productsListProps?.config?.grid?.pricing;

  const pricingList =
    customer || priceGroup
      ? configPricing
      : configPricing?.filter(i => !pricingDependsOnCustomer.includes(i.id));

  const aiSearchActive = searchContent === SearchType.AI;
  const hideFloatMenu =
    showAIInput || (productsListProps.isLoading && aiSearchActive);

  const clearCategoriesAndFacets = () => {
    headerProps?.onSelectCategory(null);
    filtersProps.onSelectFacet(false, filtersProps.selectedFacets);
  };

  const floatMenuItems = [
    {
      title: 'Filters',
      icon: FilterIcon,
      onClick: () => {
        setDrawerContent(MobileDrawerContent.Filters);
        setDrawerHeight('90%');
      },
      count:
        filtersProps?.selectedFacets?.length +
        (productsListProps?.filters?.customer?.value ? 1 : 0)
    },
    ...(!searchContent
      ? [
          {
            title: 'Search',
            icon: SearchIcon,
            onClick: () => {
              setSearchContent(SearchType.Regular);
              clearCategoriesAndFacets();
            }
          },
          ...(flags?.productsProductMatch
            ? [
                {
                  title: 'Ask AI',
                  icon: AIIcon,
                  onClick: () => {
                    setSearchContent(SearchType.AI);
                    setShowAIInput(true);
                  }
                }
              ]
            : [])
        ]
      : []),
    ...(selectedProducts.length > 0
      ? [
          {
            title: 'Email',
            count: selectedProducts.length,
            icon: EmailIcon,
            onClick: () => {
              setDrawerContent(EmailDrawerContents.Email);
              setDrawerHeight('90%');
            }
          }
        ]
      : [])
  ];

  const handleEmail = () => {
    if (isEmbedded && window.AndroidInterface) {
      try {
        const selectedProductsHTML = getSelectedProductsHTML(
          productsListProps.selectedProducts
        );
        const data = { products: selectedProductsHTML };

        window.AndroidInterface.postMessage('sendEmail', JSON.stringify(data));

        productsListProps.handleProductSelect(
          false,
          productsListProps.selectedProducts
        );
        setDrawerContent(null);
      } catch (error) {
        // Silently handle Android interface errors
      }
    } else {
      // Existing behavior
      captureAnalytics(
        {
          productSKU: selectedProducts.map(p => p.sku).join(','),
          actionType: ProductActionType.AddProductToEmail,
          appType: AnalyticsAppType.Mobile
        },
        AnalyticsModules.Catalog
      );

      const parsedSelected: EditorProduct[] =
        parseProductsToEmailProduct(selectedProducts);

      open({ products: parsedSelected }, true);
      productsListProps.handleProductSelect(false, selectedProducts);
      setShowEditor(true);
    }
  };

  const handleCloseDrawer = () => setDrawerContent(null);

  const closeSearch = () => {
    controller?.abort();
    headerProps.handleSearch({
      type: SearchType.Regular,
      search: { value: '' }
    });
    setSearchContent(null);
    setSearchQuery('');
  };

  const handlePromotionsClick = (product: Product) => {
    openDialog(
      <PromotionDialog
        product={product}
        onClose={() => closeDialog('promotion-dialog')}
      />,
      {
        id: 'promotion-dialog'
      }
    );
  };

  if (productDetailsProps.selectedProduct)
    return (
      <MobileProductDetails
        {...productDetailsProps}
        selectedProducts={selectedProducts}
        productCardProps={{
          priceType: productsListProps?.config.priceType,
          gridConfig: {
            ...productsListProps?.config.grid,
            pricing: pricingList
          },
          filters: productsListProps.filters,
          handleCheck: productsListProps.handleProductSelect
        }}
        onPromotionClick={handlePromotionsClick}
        openProductDetails={productsListProps.openProductDetails}
        pricingList={pricingList}
        disableSelect={productsListProps.disableSelect}
      />
    );

  if (showEditor)
    return (
      <MobileEditor
        handleClose={() => {
          setDrawerContent(null);
          setShowEditor(false);
        }}
      />
    );

  return (
    <>
      <Container>
        <Header
          {...headerProps}
          // viewMode={viewMode}
          // toggleViewMode={() =>
          //   setViewMode(prev =>
          //     prev === Views.LIST_VIEW ? Views.GRID_VIEW : Views.LIST_VIEW
          //   )
          // }
          searchContent={searchContent}
          searchQuery={searchQuery}
          showAIInput={showAIInput}
          closeAISearchMode={() => {
            if (!showAIInput) {
              setShowAIInput(true);
              controller?.abort();
              return;
            }
            closeSearch();
            setShowAIInput(false);
            clearCategoriesAndFacets();
          }}
          closeSearchMode={() => {
            clearCategoriesAndFacets();
            closeSearch();
          }}
          handleSearch={val => {
            headerProps.handleSearch(val);
            setSearchQuery(val.search.value);
          }}
          handleOpenCategoryDrawer={() => {
            setDrawerContent(MobileDrawerContent.Categories);
            setDrawerHeight('90%');
          }}
        />
        {searchContent === SearchType.AI && showAIInput ? (
          <AskAIInput
            handleSearch={values => {
              headerProps.handleSearch(values);
              setShowAIInput(false);
            }}
          />
        ) : (
          <MobileProductList
            {...productsListProps}
            pricingList={pricingList}
            aiSearchActive={aiSearchActive}
            onPromotionClick={handlePromotionsClick}
            // viewMode={viewMode}
          />
        )}
      </Container>
      {!hideFloatMenu && <FloatMenu items={floatMenuItems} />}
      <Drawer
        anchor="bottom"
        open={drawerContent}
        onClose={handleCloseDrawer}
        PaperProps={{
          style: {
            ...mobileDriverStyle,
            padding: drawerContent !== EmailDrawerContents.Email && 20,
            height: drawerHeight
          }
        }}>
        {drawerContent === MobileDrawerContent.Categories && (
          <MobileCategories
            handleClose={handleCloseDrawer}
            {...categoriesProps}
          />
        )}
        {drawerContent === MobileDrawerContent.Filters && (
          <MobileFilters
            filters={productsListProps.filters}
            aiSearchActive={aiSearchActive}
            handleClose={handleCloseDrawer}
            {...filtersProps}
          />
        )}
        {drawerContent === EmailDrawerContents.Email && (
          <MobileEmailSelected
            selected={selectedProducts}
            priceType={productsListProps?.config.priceType}
            filters={productsListProps.filters}
            handleEmail={handleEmail}
            aiSearchActive={aiSearchActive}
            gridConfig={{
              ...productsListProps?.config.grid,
              pricing: pricingList
            }}
            handleSelect={productsListProps.handleProductSelect}
            handleClose={() => setDrawerContent(null)}
            handleClear={() => {
              setDrawerContent(EmailDrawerContents.ClearSelected);
              setDrawerHeight(215);
            }}
          />
        )}
        {drawerContent === EmailDrawerContents.ClearSelected && (
          <MobileClearSelected
            onConfirm={() => {
              setDrawerContent(null);
              productsListProps.handleProductSelect(false, selectedProducts);
            }}
            onCancel={() => setDrawerContent(null)}
          />
        )}
      </Drawer>
    </>
  );
};
